import { useState, useEffect, useCallback } from "react";
import { getCharacterCountInfo } from "../utils/start-message-validation";
import type { CharacterCountInfo } from "../types/start-message-types";

interface UseRealTimeCharacterCounterOptions {
  initialContent?: string;
  updateInterval?: number; // milliseconds
  enableRealTimeUpdates?: boolean;
}

interface UseRealTimeCharacterCounterResult {
  content: string;
  countInfo: CharacterCountInfo;
  updateContent: (newContent: string) => void;
  isTyping: boolean;
  lastUpdated: Date | null;
}

export function useRealTimeCharacterCounter(
  options: UseRealTimeCharacterCounterOptions = {}
): UseRealTimeCharacterCounterResult {
  const {
    initialContent = "",
    updateInterval = 100, // Update every 100ms for smooth real-time feel
    enableRealTimeUpdates = true,
  } = options;

  const [content, setContent] = useState<string>(initialContent);
  const [countInfo, setCountInfo] = useState<CharacterCountInfo>(() =>
    getCharacterCountInfo(initialContent)
  );
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // Update character count info when content changes
  const updateCharacterCount = useCallback((newContent: string) => {
    const newCountInfo = getCharacterCountInfo(newContent);
    setCountInfo(newCountInfo);
    setLastUpdated(new Date());
  }, []);

  // Handle content updates with real-time feedback
  const updateContent = useCallback(
    (newContent: string) => {
      setContent(newContent);
      setIsTyping(true);

      // Clear existing timeout
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }

      if (enableRealTimeUpdates) {
        // Update immediately for real-time feedback
        updateCharacterCount(newContent);

        // Set typing indicator timeout
        const timeout = setTimeout(() => {
          setIsTyping(false);
        }, updateInterval * 3); // Stop typing indicator after 3x update interval

        setTypingTimeout(timeout);
      } else {
        // Update with debounce
        const timeout = setTimeout(() => {
          updateCharacterCount(newContent);
          setIsTyping(false);
        }, updateInterval);

        setTypingTimeout(timeout);
      }
    },
    [enableRealTimeUpdates, updateInterval, updateCharacterCount, typingTimeout]
  );

  // Update count info when initial content changes
  useEffect(() => {
    if (initialContent !== content) {
      setContent(initialContent);
      updateCharacterCount(initialContent);
    }
  }, [initialContent, content, updateCharacterCount]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  return {
    content,
    countInfo,
    updateContent,
    isTyping,
    lastUpdated,
  };
}
